import { useState } from "react";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import { SEO } from "../components/shared/SEO";
import { PageHeader } from "../components/shared/PageHeader";
import { Reveal } from "../components/shared/Reveal";
import { Field, PrivacyCheck, SuccessBox } from "../components/shared/Form";
import { PROPOSE, IMAGES } from "../content/site";
import { api, formatApiError, uploadFiles } from "../lib/api";

const FilePick = ({ label, files, onChange, accept, multiple, testId }) => (
  <div>
    <div className="label-line mb-3">{label}</div>
    <label className="flex items-center gap-4 border border-dashed border-ink/25 hover:border-gold transition-colors p-6 cursor-pointer">
      <Upload size={20} strokeWidth={1.2} className="text-stone" />
      <span className="text-sm font-light text-ink/70">{multiple ? "Seleziona una o più immagini (JPG, PNG, WEBP)" : "Seleziona un file (PDF o immagine)"}</span>
      <input type="file" data-testid={testId} accept={accept} multiple={multiple} className="hidden" onChange={(e) => onChange(multiple ? [...files, ...Array.from(e.target.files)] : Array.from(e.target.files))} />
    </label>
    {files.length > 0 && (
      <ul className="mt-3 flex flex-wrap gap-2">
        {files.map((f, i) => (
          <li key={i} className="flex items-center gap-2 text-xs bg-white border border-ink/10 px-3 py-1.5">
            {f.name}<button type="button" aria-label="Rimuovi" onClick={() => onChange(files.filter((_, j) => j !== i))}><X size={12} /></button>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const INITIAL = { name: "", phone: "", email: "", city: "", address: "", type: "", surface: "", price: "", condition: "", message: "", privacy: false };

export default function ProponiImmobile() {
  const [f, setF] = useState(INITIAL);
  const [photos, setPhotos] = useState([]);
  const [plan, setPlan] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const all = [...photos, ...plan];
      const attachments = all.length ? await uploadFiles(all) : [];
      await api.post("/leads", {
        type: "property", name: f.name, email: f.email, phone: f.phone, city: f.city, message: f.message,
        details: { indirizzo: f.address, tipologia: f.type, superficie: f.surface, prezzo_richiesto: f.price, stato_immobile: f.condition, n_foto: photos.length, planimetria: plan.length > 0 },
        attachments, privacy_accepted: f.privacy,
      });
      setSent(true);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <>
      <SEO title="Proponi un Immobile da Valorizzare | Home Haven" description="Proponi a Home Haven un immobile o un'opportunità da analizzare per riqualificazione, frazionamento, sviluppo o valorizzazione." image={IMAGES.detail1} />
      <PageHeader eyebrow="Proprietari, intermediari, professionisti" title="Hai un immobile da valorizzare?" image={IMAGES.detail1} text={`${PROPOSE.headline} ${PROPOSE.paragraphs[0]}`} />
      <section className="section bg-cream">
        <div className="container-hh grid grid-cols-1 lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-4">
            <div className="eyebrow mb-6">Cosa cerchiamo</div>
            <p className="lead">{PROPOSE.paragraphs[1]}</p>
            <ul className="mt-10 space-y-4">
              {["Immobili da ristrutturare", "Immobili frazionabili", "Proprietà con potenziale di valorizzazione", "Palazzine e interi stabili", "Immobili da mettere a reddito"].map((t) => (
                <li key={t} className="flex items-center gap-4 text-sm uppercase tracking-[0.15em] text-ink/80"><span className="w-6 h-px bg-sage" />{t}</li>
              ))}
            </ul>
          </Reveal>
          <div className="lg:col-span-7 lg:col-start-6">
            {sent ? (
              <SuccessBox testId="property-form-success" title="Proposta ricevuta." text="Grazie. Analizzeremo l'immobile e ti ricontatteremo per approfondire l'opportunità." />
            ) : (
              <form onSubmit={submit} data-testid="property-form" className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Field label="Nome e cognome" name="name" value={f.name} onChange={set} required testId="property-name" />
                <Field label="Telefono" name="phone" type="tel" value={f.phone} onChange={set} required testId="property-phone" />
                <Field label="Email" name="email" type="email" value={f.email} onChange={set} required testId="property-email" />
                <Field label="Città" name="city" value={f.city} onChange={set} required testId="property-city" />
                <div className="md:col-span-2"><Field label="Indirizzo dell'immobile" name="address" value={f.address} onChange={set} testId="property-address" /></div>
                <Field label="Tipologia" name="type" as="select" value={f.type} onChange={set} options={["Appartamento", "Intero stabile / palazzina", "Villa", "Locale commerciale", "Terreno edificabile", "Altro"]} testId="property-type" />
                <Field label="Superficie (mq)" name="surface" type="number" value={f.surface} onChange={set} testId="property-surface" />
                <Field label="Prezzo richiesto (€)" name="price" value={f.price} onChange={set} testId="property-price" />
                <Field label="Stato dell'immobile" name="condition" as="select" value={f.condition} onChange={set} options={["Da ristrutturare", "Abitabile", "Buono stato", "Ristrutturato", "Al grezzo / in costruzione"]} testId="property-condition" />
                <div className="md:col-span-2"><Field label="Descrizione" name="message" as="textarea" rows={5} value={f.message} onChange={set} placeholder="Descrivi l'immobile, il contesto e le sue potenzialità…" testId="property-message" /></div>
                <div className="md:col-span-2"><FilePick label="Fotografie" files={photos} onChange={setPhotos} accept="image/*" multiple testId="property-photos" /></div>
                <div className="md:col-span-2"><FilePick label="Planimetria" files={plan} onChange={setPlan} accept="image/*,.pdf" testId="property-floorplan" /></div>
                <div className="md:col-span-2"><PrivacyCheck checked={f.privacy} onChange={(v) => set("privacy", v)} testId="property-privacy" /></div>
                <div className="md:col-span-2"><button type="submit" disabled={busy} data-testid="property-submit" className="btn-primary disabled:opacity-50">{busy ? "Invio in corso…" : "Proponi il tuo immobile"}</button></div>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
